import { createContext, useContext } from "react";

/**
 * How the transcript follows the bottom while content grows.
 *
 * - `instant` — pin `scrollTop` synchronously in ResizeObserver.
 * - `spring` — ease toward the moving bottom with a rAF spring (production).
 */
export type ScrollFollowMode = "instant" | "spring";

/**
 * Spring integration used while `mode` is `spring`.
 *
 * - `velocity` — second-order damped oscillator that carries velocity across
 *   retargets (continuous under fast streaming growth).
 * - `lerp` — memoryless exponential ease of remaining distance.
 */
export type SpringMotion = "velocity" | "lerp";

/**
 * Fraction of remaining distance closed per {@link SPRING_LERP_FRAME_MS}
 * (calibrated for 60Hz). Actual steps scale by elapsed time so 120Hz and 60Hz
 * catch up in the same wall-clock duration.
 */
export const DEFAULT_SPRING_LERP = 0.08;
/** Nominal frame duration the lerp knob is calibrated against (60Hz). */
export const SPRING_LERP_FRAME_MS = 1000 / 60;
/** Cap a single integration step so a backgrounded tab does not jump. */
export const SPRING_MAX_STEP_MS = 64;
/** Minimum progress per calibrated frame, preventing stalls after pixel rounding. */
export const SPRING_MIN_STEP_PX = 0.5;

/**
 * Perceptual catch-up time for the velocity spring (~1/ω). Wall-clock settling
 * is a few multiples of this under critical damping.
 */
export const DEFAULT_SPRING_RESPONSE_MS = 180;
/** Critical damping — settles at the bottom without oscillating past it. */
export const DEFAULT_SPRING_DAMPING_RATIO = 1;
/** Default spring integration while following. */
export const DEFAULT_SPRING_MOTION: SpringMotion = "velocity";
/**
 * Velocity below this (px/ms) counts as settled when position is also within
 * the settle epsilon — stops the rAF loop from creeping forever.
 */
export const SPRING_VELOCITY_SETTLE_PX_PER_MS = 0.001;

/**
 * Slack (in px) within which a scroll container counts as "at the bottom"
 * for auto-following new content: the transcript (`useScrollManager`) and its
 * nested follow windows (`useFollowBottomScroll`) re-pin only when the user
 * is genuinely parked there.
 */
export const BOTTOM_THRESHOLD_PX = 70;
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Time-scaled exponential ease: `lerp` is the fraction closed per
 * {@link SPRING_LERP_FRAME_MS}; returns the fraction to close over `dtMs`.
 */
export function springStepAlpha(lerp: number, dtMs: number): number {
  if (lerp <= 0) return 0;
  if (lerp >= 1) return 1;
  const dt = Math.min(Math.max(dtMs, 0), SPRING_MAX_STEP_MS);
  if (dt === 0) return 0;
  return 1 - Math.pow(1 - lerp, dt / SPRING_LERP_FRAME_MS);
}

/** Distance to move this frame, with a refresh-rate-independent minimum. */
export function springStepDistance(deltaPx: number, lerp: number, dtMs: number): number {
  if (deltaPx === 0) return 0;
  const dt = Math.min(Math.max(dtMs, 0), SPRING_MAX_STEP_MS);
  const easedDistance = Math.abs(deltaPx) * springStepAlpha(lerp, dt);
  const minimumDistance = SPRING_MIN_STEP_PX * (dt / SPRING_LERP_FRAME_MS);
  return Math.sign(deltaPx) * Math.min(Math.abs(deltaPx), Math.max(easedDistance, minimumDistance));
}
export type VelocitySpringStep = {
  stepPx: number;
  velocityPxPerMs: number;
};

/**
 * Closed-form damped-oscillator step toward a fixed target for this frame.
 *
 * `deltaPx` is `target - current` (positive when scrolling down). Velocity is
 * `d(scrollTop)/dt` in px/ms and is preserved across retargets by the caller —
 * only this integration updates it. Uses the analytic critically / overdamped
 * solution so two half-frames match one full frame (refresh-rate independent).
 * `dampingRatio` is clamped to >= 1 so the step never oscillates past the
 * target.
 */
export function velocitySpringStep(deltaPx: number, velocityPxPerMs: number, responseMs: number, dampingRatio: number, dtMs: number): VelocitySpringStep {
  const dt = Math.min(Math.max(dtMs, 0), SPRING_MAX_STEP_MS);
  if (deltaPx === 0 && Math.abs(velocityPxPerMs) < SPRING_VELOCITY_SETTLE_PX_PER_MS) {
    return {
      stepPx: 0,
      velocityPxPerMs: 0
    };
  }
  if (dt === 0) {
    return {
      stepPx: 0,
      velocityPxPerMs: velocityPxPerMs
    };
  }
  const response = Math.max(responseMs, 1);
  const omega = 1 / response;
  const zeta = Math.max(dampingRatio, 1);

  // Error coordinate: x = current - target = -delta. Want x → 0.
  const x0 = -deltaPx;
  const v0 = velocityPxPerMs;
  let x1: number;
  let v1: number;
  if (zeta === 1 || Math.abs(zeta - 1) < 1e-9) {
    // Critically damped: x(t) = (x0 + (v0 + ω x0) t) e^(-ω t)
    const exp = Math.exp(-omega * dt);
    const b = v0 + omega * x0;
    x1 = (x0 + b * dt) * exp;
    v1 = (v0 - omega * b * dt) * exp;
  } else {
    // Overdamped: x(t) = C1 e^(r1 t) + C2 e^(r2 t)
    const alpha = zeta * omega;
    const beta = omega * Math.sqrt(zeta * zeta - 1);
    const r1 = -alpha + beta;
    const r2 = -alpha - beta;
    const denom = r1 - r2;
    const c1 = (v0 - r2 * x0) / denom;
    const c2 = x0 - c1;
    const e1 = Math.exp(r1 * dt);
    const e2 = Math.exp(r2 * dt);
    x1 = c1 * e1 + c2 * e2;
    v1 = c1 * r1 * e1 + c2 * r2 * e2;
  }
  let stepPx = x1 - x0;

  // Floor creeping progress so sub-pixel rounding cannot stall follow, without
  // allowing the step to pass the remaining distance to the target.
  const minimumDistance = SPRING_MIN_STEP_PX * (dt / SPRING_LERP_FRAME_MS);
  if (deltaPx !== 0 && Math.abs(stepPx) < minimumDistance) {
    const towardTarget = Math.sign(deltaPx) * minimumDistance;
    stepPx = Math.sign(deltaPx) * Math.min(Math.abs(deltaPx), Math.abs(towardTarget));
    // Keep velocity consistent with a minimum crawl toward the target.
    if (Math.sign(v1) !== Math.sign(deltaPx) || Math.abs(v1) < SPRING_VELOCITY_SETTLE_PX_PER_MS) {
      v1 = stepPx / dt;
    }
  }

  // Hard clamp: never move past the target in one step when critically /
  // overdamped (analytic drift or the min-step floor could otherwise).
  if (deltaPx !== 0 && Math.sign(stepPx) === Math.sign(deltaPx)) {
    if (Math.abs(stepPx) >= Math.abs(deltaPx)) {
      return {
        stepPx: deltaPx,
        velocityPxPerMs: 0
      };
    }
  } else if (deltaPx !== 0 && Math.sign(stepPx) !== 0 && Math.sign(stepPx) !== Math.sign(deltaPx)) {
    // Should not happen for ζ >= 1 from rest; kill reverse motion.
    return {
      stepPx: 0,
      velocityPxPerMs: 0
    };
  }
  return {
    stepPx,
    velocityPxPerMs: v1
  };
}

/** Snap instead of easing when farther than this from the bottom. */
export const DEFAULT_SPRING_SNAP_DISTANCE_PX = 480;

/**
 * Breathing room above the anchored user message when turn-anchor is on.
 * Clears the transcript's top fade (`h-3` / 12px) plus a bit of air so the
 * bubble is not clipped or washed out at the viewport edge. Raise this to
 * bleed more of the previous turn above the send.
 */
export const TURN_ANCHOR_TOP_OFFSET_PX = 96;

/** Production default: ChatGPT-style turn anchoring ships on. */
export const DEFAULT_TURN_ANCHOR = true;

/** Chat prose line height (`type-chat-prose` in globals.css). */
const TRANSCRIPT_LINE_HEIGHT_PX = 22;
/** Prose row gap (see transcriptRowGap) / transcript bottom padding (`pb-6`). */
const TRANSCRIPT_MESSAGE_GAP_PX = 24;

/**
 * Reserved reply room below the user bubble on send. The spacer scrolls only
 * the deficit to reach this height (capped by the full bleed-to-top hoist),
 * so short replies often land without continuous scroll.
 *
 * Sized as seventeen prose lines plus the bubble→reply gap and the
 * transcript's bottom padding (422px): a working turn's opening — an
 * acknowledgement line (22px), the live-stretch window (176px clamp), and
 * the anvil's fixed bottom row (h-12 + mt-2 = 56px), 302px with both gaps —
 * fits inside the reserve, so the view stays still until durable narrative
 * accumulates. A thinking-only opening fills far less, so a larger reserve
 * reads as dead air below the bubble.
 */
export const DEFAULT_TURN_ANCHOR_HEADROOM_PX = 17 * TRANSCRIPT_LINE_HEIGHT_PX + 2 * TRANSCRIPT_MESSAGE_GAP_PX;

/**
 * How the viewport reaches the anchored send position.
 *
 * - `instant` — size spacer, snap scroll to bottom.
 * - `spring` — size spacer, ease to bottom with the active spring motion.
 * - `grow` — animate the spacer from 0 → target while pinned to bottom so
 *   prior content appears to slide up under the send.
 */
export type TurnAnchorSendMotion = "instant" | "spring" | "grow";

/**
 * Spring eases the scroll to the anchored position through the scroll
 * system's velocity spring (skipping the streaming snap threshold), so the
 * send motion matches the feel of bottom-follow.
 */
export const DEFAULT_TURN_ANCHOR_SEND_MOTION: TurnAnchorSendMotion = "spring";

/**
 * Time-scale for the send ease relative to streaming follow: the anchor
 * hoist settles in one-sixth of the follow spring's duration, reading as a
 * quick dart to the anchored position rather than the streaming glide.
 */
export const TURN_ANCHOR_SEND_RESPONSE_FACTOR = 1 / 6;
export type TurnAnchorSpacerInput = {
  /** Visible height of the scroll viewport. */
  clientHeight: number;
  /** Height of natural transcript content (messages only — no spacer). */
  naturalContentHeight: number;
  /**
   * Offset of the target user message from the top of the natural content
   * (`offsetTop` relative to the natural wrapper).
   */
  userMsgOffsetTop: number;
  /** Height of the target user message bubble. */
  userMsgHeight?: number;
  /** Breathing room above the bubble when pinned (full-hoist cap). */
  topOffsetPx?: number;
  /**
   * Minimum reply room below the bubble bottom. Scrolls only the deficit,
   * capped by the full bleed-to-top hoist. Omitted or `Infinity` keeps
   * full-anchor sizing.
   */
  headroomPx?: number;
  /**
   * Fixed content between the natural stack bottom and the scrollable
   * bottom, excluding the spacer itself (content padding, overflow-anchor
   * sentinel). On a bottom pin it reads as extra room under the bubble, so
   * it offsets both the hoist and the headroom deficit.
   */
  bottomChromePx?: number;
};

/**
 * Spacer height for turn-anchor send. Full hoist puts the bubble at
 * `topOffsetPx` from the viewport top; reserved headroom clamps that so
 * the spacer only covers the deficit to `headroomPx` of room below the
 * bubble. Returns 0 when natural room already meets the target (or the
 * stack already fills the viewport).
 *
 * `stackBelow = naturalContentHeight - userMsgOffsetTop` is the bubble plus
 * everything after it. `naturalSpaceBelowMsg = stackBelow - userMsgHeight`
 * is free room under the bubble before the spacer.
 */
export function computeTurnAnchorSpacerPx({
  clientHeight,
  naturalContentHeight,
  userMsgOffsetTop,
  userMsgHeight = 0,
  topOffsetPx = TURN_ANCHOR_TOP_OFFSET_PX,
  headroomPx = Infinity,
  bottomChromePx = 0
}: TurnAnchorSpacerInput): number {
  if (!Number.isFinite(clientHeight) || !Number.isFinite(naturalContentHeight) || !Number.isFinite(userMsgOffsetTop) || clientHeight <= 0) {
    return 0;
  }
  const chrome = Math.max(0, Number.isFinite(bottomChromePx) ? bottomChromePx : 0);
  const stackBelow = Math.max(0, naturalContentHeight - userMsgOffsetTop);
  const fullSpacer = Math.max(0, clientHeight - topOffsetPx - stackBelow - chrome);
  if (!Number.isFinite(headroomPx) || headroomPx === Infinity) {
    return fullSpacer;
  }
  const msgH = Math.max(0, Number.isFinite(userMsgHeight) ? userMsgHeight : 0);
  const naturalSpaceBelowMsg = Math.max(0, stackBelow - msgH);
  const headroomSpacer = Math.max(0, headroomPx - naturalSpaceBelowMsg - chrome);
  return Math.min(fullSpacer, headroomSpacer);
}
export type ScrollFollowConfig = {
  mode: ScrollFollowMode;
  /** Spring integration while `mode` is `spring`. Defaults to velocity. */
  springMotion?: SpringMotion;
  /**
   * Fraction of remaining distance closed per 60Hz frame (0–1). Used when
   * `springMotion` is `lerp`. Steps are scaled by elapsed time so the catch-up
   * rate is refresh-rate independent.
   */
  lerp?: number;
  /**
   * Perceptual catch-up time in ms (~1/ω) for the velocity spring. Used when
   * `springMotion` is `velocity`.
   */
  responseMs?: number;
  /**
   * Damping ratio for the velocity spring (>= 1 = no overshoot). Used when
   * `springMotion` is `velocity`.
   */
  dampingRatio?: number;
  /** Override for spring snap distance in px. */
  snapDistancePx?: number;
  /**
   * When true, a send sizes a bottom spacer so the user message lands near
   * the viewport top; the spacer shrinks as the reply grows (zero scroll
   * until overflow), then existing spring/instant follow takes over.
   */
  turnAnchor?: boolean;
  /**
   * Pixels of viewport above the anchored user message. Larger values bleed
   * more of the previous turn into view. Defaults to
   * {@link TURN_ANCHOR_TOP_OFFSET_PX}.
   */
  turnAnchorTopOffsetPx?: number;
  /**
   * Motion used to reach the anchored position on send. Defaults to
   * {@link DEFAULT_TURN_ANCHOR_SEND_MOTION}.
   */
  turnAnchorSendMotion?: TurnAnchorSendMotion;
  /**
   * Minimum reply room (px) below the user bubble on send. Scrolls only the
   * deficit, capped by the full bleed hoist. Defaults to
   * {@link DEFAULT_TURN_ANCHOR_HEADROOM_PX}. Pass `Infinity` for full anchor.
   */
  turnAnchorHeadroomPx?: number;
};
export const ScrollFollowContext = createContext<ScrollFollowConfig>({
  mode: "spring",
  springMotion: DEFAULT_SPRING_MOTION,
  lerp: DEFAULT_SPRING_LERP,
  responseMs: DEFAULT_SPRING_RESPONSE_MS,
  dampingRatio: DEFAULT_SPRING_DAMPING_RATIO,
  turnAnchor: DEFAULT_TURN_ANCHOR,
  turnAnchorTopOffsetPx: TURN_ANCHOR_TOP_OFFSET_PX,
  turnAnchorSendMotion: DEFAULT_TURN_ANCHOR_SEND_MOTION,
  turnAnchorHeadroomPx: DEFAULT_TURN_ANCHOR_HEADROOM_PX
});
export function useScrollFollowConfig() {
  return useContext(ScrollFollowContext);
}
export function useScrollFollowMode() {
  return useScrollFollowConfig().mode;
}
export function useTurnAnchorEnabled() {
  return useScrollFollowConfig().turnAnchor ?? DEFAULT_TURN_ANCHOR;
}
export function useTurnAnchorTopOffsetPx() {
  const value = useScrollFollowConfig().turnAnchorTopOffsetPx;
  if (value == null || !Number.isFinite(value)) {
    return TURN_ANCHOR_TOP_OFFSET_PX;
  }
  return Math.max(0, value);
}
export function useTurnAnchorSendMotion() {
  const enabled = useTurnAnchorEnabled();
  const value = useScrollFollowConfig().turnAnchorSendMotion;
  if (!enabled) {
    return "instant";
  }
  return value === "instant" || value === "spring" || value === "grow" ? value : DEFAULT_TURN_ANCHOR_SEND_MOTION;
}

/**
 * Reserved reply room below the bubble on send. `Infinity` keeps the full
 * bleed-to-top hoist; unset / NaN / negative falls back to the default.
 */
export function useTurnAnchorHeadroomPx() {
  const value = useScrollFollowConfig().turnAnchorHeadroomPx;
  if (value === Infinity) {
    return Infinity;
  }
  if (value == null || !Number.isFinite(value) || value < 0) {
    return DEFAULT_TURN_ANCHOR_HEADROOM_PX;
  }
  return value;
}